import {
	GQLMerchDecorationFragment,
	GQLMerchTileFragment,
	GQLOperationalSettingsFragment,
	GQLOrderItemFragment,
	GQLOrderItemPrintFragment,
	GQLOrderItemTileFragment,
	GQLProductBaseFragment,
	GQLProductFragment,
	GQLProductTileFragment,
	GQLStoreBaseFragment,
} from '@theprintbar/graphql';
import {
	Breakdown,
	Decoration,
	DecorationServer,
	Merch,
	MerchFormServer,
	decorationIsSamedayCompatible,
	getDecorationContent,
	getDecorationPrice,
	getPartnerPortalPrice,
	getSamedayName,
	isDTFDec,
	isDTGDec,
	isEMBDec,
	isMerchDecoration,
	isMerchTileGuard,
	isOrderItemPrint,
	isSCRDec,
	merchDTFCount,
	merchDTGCount,
	merchEMBCount,
	merchEMBDecorations,
	merchSCRCount,
	orderItemPrintToDecoration,
} from '..';
import { capitalise, cloneDeep, getCurrencyValue } from '../..';
import { orderItemDTFCount, orderItemDTGCount, orderItemEMBCount, orderItemSCRCount } from '../orders/orderItemChecks';

export interface GetMerchPriceBaseProps {
	quantity?: number;
	settings: GQLOperationalSettingsFragment;
	isSameday?: boolean;
	ignoreDigitization?: boolean;
	store?: GQLStoreBaseFragment | null;
}

interface GetMerchTilePriceProps extends GetMerchPriceBaseProps {
	merch?: GQLMerchTileFragment | null;
	product?: GQLProductFragment;
	variantId?: string;
}

interface GetMerchPriceProps extends GetMerchPriceBaseProps {
	merch?: Merch | MerchFormServer | GQLMerchTileFragment | null;
	product: GQLProductBaseFragment;
}

export type MerchPrice = ReturnType<typeof getMerchTilePrice>;
export function getMerchTilePrice({ merch, variantId, ...props }: GetMerchTilePriceProps) {
	const emptyCost = { breakdown: [], subtotal: 0, total: 0, singlePrice: 0, quantity: props.quantity || 1 };
	if (!merch) return emptyCost;

	const suppliedMerch = props.store?.suppliedMerch?.find(o => o.merch.id === merch?.id);
	if (suppliedMerch) {
		const variantSupplied = variantId && suppliedMerch.variants.find(v => v.variant.id === variantId);
		const allVariantsSupplied = props.product && suppliedMerch.variants.length === props.product.variants?.length;
		if (allVariantsSupplied || variantSupplied || !props.product) {
			return emptyCost;
		}
	}

	return getMerchPrice({ ...props, merch, product: merch.product });
}

export function getMerchPrice(props: GetMerchPriceProps) {
	const { merch, product, quantity = 1, settings, isSameday, store } = props;

	if (!merch || quantity === 0) {
		return { breakdown: [], quantity, subtotal: 0, total: 0, singlePrice: 0 };
	}

	let breakdown: Breakdown[] = [getProductCost({ product, quantity, store })];
	if (isSameday && !store) {
		breakdown.push(getSamedayCost({ quantity, settings }));
	}

	merch.decorations.forEach(decoration => {
		if (isSameday && !decorationIsSamedayCompatible(decoration)) return;
		if (isMerchDecoration(decoration)) {
			const side = decoration.side?.side;
			if (!side || !decoration.design?.image?.url) return;
			const colours = decoration.requestedColours;
			breakdown = addDecorationCost({
				breakdown,
				side,
				colourCount: colours || 0,
				decoration,
				quantity,
				settings,
				store,
				product,
			});
		} else {
			const side = decoration.side;
			if (!side || !decoration.artwork?.size) return;
			const colours = decoration.meta.requestedColours;
			breakdown = addDecorationCost({
				breakdown,
				side,
				colourCount: colours || 0,
				decoration,
				quantity,
				settings,
				store,
				product,
			});
		}
	});

	const embDecorations = merchEMBDecorations(merch).filter(d => {
		if (isMerchDecoration(d)) {
			return !d.useSuppliedEmbFile;
		} else {
			return !d.meta.useSuppliedEmbFile;
		}
	})?.length;

	if (!!embDecorations && !props.ignoreDigitization) {
		breakdown = addEmbDigitizationCharges({ breakdown, embDecorations, quantity, ...props });
	}

	let subtotal = getCurrencyValue(breakdown.reduce((acc, { amount }) => acc + amount, 0));
	//THIS MUST COME LAST
	breakdown = addProductDiscount({ breakdown, ...props });
	const total = getCurrencyValue(breakdown.reduce((acc, { amount }) => acc + amount, 0));
	return {
		breakdown,
		quantity,
		subtotal,
		singlePrice: getCurrencyValue(subtotal / quantity),
		total,
	};
}

interface ProductCostProps {
	product: Pick<GQLProductFragment, 'id' | 'name' | 'salePrice'>;
	quantity: number;
	store?: GQLStoreBaseFragment | null;
}

export function getProductBreakdown(breakdown: Breakdown[]) {
	const nonProductBreakdown = [
		...getSamedayBreakdown(breakdown),
		...getDecorationBreakdown(breakdown),
		...getDigitisationBreakdown(breakdown),
		...getProductDiscountBreakdown(breakdown),
	];

	return breakdown.filter(b => !nonProductBreakdown.includes(b));
}
export function getProductCost({ product, quantity, store }: ProductCostProps) {
	const storeOverride = store?.productPrices?.find(o => o.product.id === product.id);
	let singleAmount = getCurrencyValue(storeOverride?.salePrice || product.salePrice);

	const amount = singleAmount * quantity;
	return {
		title: `${quantity}x ${product.name}`,
		singleAmount: getCurrencyValue(store && !storeOverride ? getPartnerPortalPrice(singleAmount, store) : singleAmount),
		amount: getCurrencyValue(store && !storeOverride ? getPartnerPortalPrice(amount, store) : amount),
	};
}

interface SamedayCostProps {
	settings: GQLOperationalSettingsFragment;
	quantity: number;
}

export function getSamedayBreakdown(breakdown: Breakdown[]) {
	return breakdown.filter(b => b.title.includes('Surcharge'));
}

export function getSamedayCost({ quantity, settings }: SamedayCostProps) {
	const samedayName = getSamedayName();
	return {
		title: `${quantity}x ${samedayName} Surcharge`,
		singleAmount: getCurrencyValue(settings.samedaySurcharge),
		amount: getCurrencyValue(settings.samedaySurcharge) * quantity,
	};
}

interface AddDecorationCostProps {
	breakdown: Breakdown[];
	side: string;
	colourCount: number;
	decoration: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment;
	quantity: number;
	settings: GQLOperationalSettingsFragment;
	store?: GQLStoreBaseFragment | null;
	product?: Pick<
		GQLProductTileFragment,
		| 'id'
		| 'name'
		| 'salePrice'
		| 'customDecorationName'
		| 'customDecorationDescription'
		| 'customDecorationIcon'
		| 'DTGSurcharge'
		| 'customDecorationBasePrice'
		| 'customerDecorationCostPerColour'
	>;
	dontCheckArtwork?: boolean;
}

export function getDecorationBreakdown(breakdown: Breakdown[]) {
	return breakdown.filter(b => b.title.includes('Decoration') || b.title.includes('Setup'));
}
export function addDecorationCost({
	breakdown,
	side,
	colourCount,
	decoration,
	quantity,
	settings,
	store,
	product,
	dontCheckArtwork,
}: AddDecorationCostProps) {
	const decorationprice = getDecorationPrice({ settings, decoration, store, product, dontCheckArtwork });
	breakdown.push({
		title: `${quantity}x ${capitalise(side)} ${
			getDecorationContent(isOrderItemPrint(decoration) ? decoration.decoration : decoration.type, settings, product).title
		} Decoration`,
		singleAmount: getCurrencyValue(decorationprice),
		amount: getCurrencyValue(decorationprice * quantity),
	});

	if (isSCRDec(decoration, true)) {
		const scrCost = settings.scrPricePerScreen * colourCount;
		const singlePrice = scrCost / quantity;
		breakdown.push({
			title: `${colourCount}x ${capitalise(side)} Screen Setup`,
			singleAmount: getCurrencyValue(!!store ? getPartnerPortalPrice(singlePrice, store) : singlePrice),
			amount: getCurrencyValue(!!store ? getPartnerPortalPrice(scrCost, store) : scrCost),
		});
	}

	return breakdown;
}

interface AddEmbCostsProps extends GetMerchPriceProps {
	breakdown: Breakdown[];
	embDecorations: number;
	quantity: number;
}

export function getDigitisationBreakdown(breakdown: Breakdown[]) {
	return breakdown.filter(b => b.title.includes('Embroidery Digitisation'));
}
export function addEmbDigitizationCharges({ breakdown, settings, quantity, store, embDecorations }: AddEmbCostsProps) {
	const embCost = getCurrencyValue(parseFloat(settings.embDigitizingCost) * embDecorations);
	const singlePrice = embCost / quantity;
	breakdown.push({
		title: `${embDecorations}x Embroidery Digitisation`,
		singleAmount: getCurrencyValue(!!store ? getPartnerPortalPrice(singlePrice, store) : singlePrice),
		amount: getCurrencyValue(!!store ? getPartnerPortalPrice(embCost, store) : embCost),
	});
	return breakdown;
}
interface AddProductDiscountProps extends GetMerchPriceProps {
	breakdown: Breakdown[];
	orderItem?: GQLOrderItemTileFragment;
}

export function getProductDiscountBreakdown(breakdown: Breakdown[]) {
	return breakdown.filter(b => b.title.includes('Volume discount'));
}
export function addProductDiscount({ breakdown, merch, orderItem, product, store, settings, quantity }: AddProductDiscountProps) {
	if (!quantity || (!orderItem && !merch) || !!store || product.customDecorationName) {
		return breakdown;
	}

	const productTotal = getProductCost({ product, quantity, store });
	const breakdownTotal = getCurrencyValue(breakdown.reduce((acc, { amount }) => acc + amount, 0));
	let discountTotal = 0;

	const dtfBook = getDtfBook(settings, quantity);
	const dtgBook = getDtgBook(settings, quantity);

	const embCount = orderItem ? orderItemEMBCount(orderItem) : merchEMBCount(merch);
	const scrCount = orderItem ? orderItemSCRCount(orderItem) : merchSCRCount(merch);
	const dtfCount = orderItem ? orderItemDTFCount(orderItem) : merchDTFCount(merch);
	const dtgCount = orderItem ? orderItemDTGCount(orderItem) : merchDTGCount(merch);

	//Add the unit price discount for each product over 20
	if (!!embCount && quantity >= 20) {
		const discountedProductCount = quantity - 19;
		discountTotal += getCurrencyValue(parseFloat(settings.embUnitDiscount) * discountedProductCount);
	} else if (!!scrCount) {
		//Discount each unit by the set percentage
		const unitDiscountPercentage = parseFloat(settings.scrUnitDiscount) / 100;
		const perUnitDiscount = getCurrencyValue(parseFloat(product.salePrice) * unitDiscountPercentage);
		discountTotal += getCurrencyValue(quantity * perUnitDiscount);
	} else if (!!dtfCount && !!dtfBook?.discount) {
		//First 4 are full price
		const totalMinus = productTotal.amount - productTotal.singleAmount * 4;
		discountTotal += getCurrencyValue(totalMinus * dtfBook.discount);
	} else if (!!dtgCount && !!dtgBook?.discount) {
		//First 4 are full price
		const totalMinus = productTotal.amount - productTotal.singleAmount * 4;
		discountTotal += getCurrencyValue(totalMinus * dtgBook.discount);
	}

	//Add the decoration discounts
	if (orderItem) {
		orderItem.prints?.forEach(print => {
			const decoration = orderItemPrintToDecoration(print);
			const decPrice = getDecorationPrice({ settings, decoration, store, product, dontCheckArtwork: true });
			if (isSCRDec(decoration)) {
				const priceBook = getScreenBook(settings, quantity, decoration.meta.requestedColours || 0);
				discountTotal += getCurrencyValue((priceBook?.discount || 0) * quantity);
			} else if (isEMBDec(decoration)) {
				for (let i = 1; i < quantity + 1; i++) {
					const priceBook = getEmbBook(settings, i, decoration.meta.stitches || 0);
					discountTotal += getCurrencyValue(priceBook?.discount || 0);
				}
			} else if (isDTGDec(decoration)) {
				const priceBook = getDtgBook(settings, quantity);
				discountTotal += getCurrencyValue(decPrice * (priceBook?.discount || 0) * quantity);
			} else if (isDTFDec(decoration)) {
				const priceBook = getDtfBook(settings, quantity);
				discountTotal += getCurrencyValue(decPrice * (priceBook?.discount || 0) * quantity);
			}
		});
	} else if (!merch) {
		throw new Error('No merch provided for discount calculation');
	} else if (isMerchTileGuard(merch)) {
		merch.decorations.forEach(decoration => {
			const decPrice = getDecorationPrice({ settings, decoration, store, product: merch.product });
			if (isSCRDec(decoration, true)) {
				const priceBook = getScreenBook(settings, quantity, decoration.requestedColours || 0);
				discountTotal += getCurrencyValue((priceBook?.discount || 0) * quantity);
			} else if (isEMBDec(decoration, true)) {
				for (let i = 1; i < quantity + 1; i++) {
					const priceBook = getEmbBook(settings, i, decoration.stitches || 0);
					discountTotal += getCurrencyValue(priceBook?.discount || 0);
				}
			} else if (isDTGDec(decoration, true)) {
				const priceBook = getDtgBook(settings, quantity);
				discountTotal += getCurrencyValue(decPrice * (priceBook?.discount || 0) * quantity);
			} else if (isDTFDec(decoration, true)) {
				const priceBook = getDtfBook(settings, quantity);
				discountTotal += getCurrencyValue(decPrice * (priceBook?.discount || 0) * quantity);
			}
		});
	} else {
		merch.decorations.forEach(decoration => {
			const decPrice = getDecorationPrice({ settings, decoration, store, product });
			if (isSCRDec(decoration, true)) {
				const priceBook = getScreenBook(settings, quantity, decoration.meta.requestedColours || 0);
				discountTotal += getCurrencyValue((priceBook?.discount || 0) * quantity);
			} else if (isEMBDec(decoration, true)) {
				//Loop each item and add the discount
				for (let i = 1; i < quantity + 1; i++) {
					const priceBook = getEmbBook(settings, i, decoration.meta.stitches || 0);
					discountTotal += getCurrencyValue(priceBook?.discount || 0);
				}
			} else if (isDTGDec(decoration, true)) {
				const priceBook = getDtgBook(settings, quantity);
				discountTotal += getCurrencyValue(decPrice * (priceBook?.discount || 0) * quantity);
			} else if (isDTFDec(decoration, true)) {
				const priceBook = getDtfBook(settings, quantity);
				discountTotal += getCurrencyValue(decPrice * (priceBook?.discount || 0) * quantity);
			}
		});
	}

	if (!!discountTotal) {
		//Get the percentage difference to the nearest 0.5%
		const percentageDifference = Math.round((discountTotal / breakdownTotal) * 200) / 2;
		const newTotal = getCurrencyValue((percentageDifference / 100) * breakdownTotal);

		breakdown.push({
			title: `Volume discount of ${percentageDifference}%`,
			singleAmount: -newTotal / quantity,
			amount: -newTotal,
		});
	}

	return breakdown;
}

export function getDtfBook(settings: GQLOperationalSettingsFragment, quantity: number) {
	return settings.dtfQuantityDiscounts.structure?.reduce(
		(acc, item) => {
			if (!item?.minQuantity || !item.percentage) return acc;

			if (quantity < item.minQuantity) return acc;
			if (acc.quantity < item.minQuantity) {
				acc.discount = parseFloat(item.percentage) / 100;
				acc.quantity = item.minQuantity;
			}

			return acc;
		},
		{ discount: 0, quantity: 0 } as { discount: number; quantity: number },
	);
}

export function getDtgBook(settings: GQLOperationalSettingsFragment, quantity: number) {
	return settings.dtgQuantityDiscounts.structure?.reduce(
		(acc, item) => {
			if (!item?.minQuantity || !item.percentage) return acc;

			if (quantity < item.minQuantity) return acc;
			if (acc.quantity < item.minQuantity) {
				acc.discount = parseFloat(item.percentage) / 100;
				acc.quantity = item.minQuantity;
			}

			return acc;
		},
		{ discount: 0, quantity: 0 } as { discount: number; quantity: number },
	);
}

export function getScreenBook(settings: GQLOperationalSettingsFragment, quantity: number, colours: number) {
	return settings.scrQuantityDiscounts.structure?.reduce(
		(acc, item) => {
			if (!item?.minQuantity) return acc;

			if (quantity < item.minQuantity) return acc;
			if (acc.quantity < item.minQuantity) {
				const baseColourPrice = [
					settings.scrOneColourPrice,
					settings.scrTwoColourPrice,
					settings.scrThreeColourPrice,
					settings.scrFourColourPrice,
					settings.scrFiveColourPrice,
					settings.scrSixColourPrice,
				][colours - 1];
				const discountColourPrice = [
					item.oneColourPrice,
					item.twoColourPrice,
					item.threeColourPrice,
					item.fourColourPrice,
					item.fiveColourPrice,
					item.sixColourPrice,
				][colours - 1];
				if (discountColourPrice) {
					acc.discount = getCurrencyValue(baseColourPrice) - getCurrencyValue(discountColourPrice);
					acc.quantity = item.minQuantity;
				}
			}

			return acc;
		},
		{ discount: 0, quantity: 0 } as { discount: number; quantity: number },
	);
}

export function getEmbBook(settings: GQLOperationalSettingsFragment, quantity: number, stitches: number) {
	return settings.embQuantityDiscounts.structure?.reduce(
		(acc, item) => {
			if (!item?.minQuantity) return acc;

			if (quantity < item.minQuantity) return acc;
			if (acc.quantity < item.minQuantity) {
				//Round up to the nearest 1000
				const roundedStitches = Math.ceil(stitches / 1000);

				if (roundedStitches < 5000 && item.underFive) {
					acc.discount = parseFloat(settings.embPriceUnderFive) - parseFloat(item.underFive);
				} else if (roundedStitches < 10000 && item.underTen) {
					acc.discount = parseFloat(settings.embPriceUnderTen) - parseFloat(item.underTen);
				} else if (roundedStitches < 15000 && item.underFifteen) {
					acc.discount = parseFloat(settings.embPriceUnderFifteen) - parseFloat(item.underFifteen);
				} else if (roundedStitches < 20000 && item.underTwenty) {
					acc.discount = parseFloat(settings.embPriceUnderTwenty) - parseFloat(item.underTwenty);
				} else if (item.overTwenty) {
					acc.discount = parseFloat(settings.embPriceOverTwenty) - parseFloat(item.overTwenty);
				}
				acc.quantity = item.minQuantity;
			}

			return acc;
		},
		{ discount: 0, quantity: 0 } as { discount: number; quantity: number },
	);
}
